import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/api";
import { useCart } from "@/context/CartContext";
import { Reveal, Overline, Magnetic } from "@/components/Motion";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQtyLocal] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === false) return <main className="pt-40 px-6 md:px-12 min-h-screen"><p className="font-mono text-sm">Product not found.</p></main>;
  if (!p) return <main className="pt-40 px-6 md:px-12 min-h-screen"><p className="font-mono text-sm text-ink/40">Loading…</p></main>;

  const soldOut = p.status === "Sold Out" || p.quantity <= 0;

  return (
    <main data-testid="product-detail" className="pt-28 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Link to="/shop" data-testid="back-to-shop" className="link-line inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60">
        <ArrowLeft size={14} /> Shop
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-10 items-start">
        <Reveal className="lg:col-span-7">
          <div className="frame-clip spotlight bg-linen border border-ink/10 p-6 md:p-10">
            <img src={p.images?.[0]} alt={p.name} className="w-full h-auto object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <Overline>{p.category}</Overline>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-tight">{p.name}</h1>
          <p className="font-display text-3xl mt-6">{inr(p.price)}</p>
          <p className={`font-mono text-[10px] uppercase tracking-[0.25em] mt-2 ${soldOut ? "text-brand" : "text-ink/50"}`}>
            {soldOut ? "Sold out" : `${p.status} · ${p.quantity} in studio`}
          </p>
          <p className="mt-8 text-ink/60 leading-relaxed">{p.description}</p>
          <dl className="mt-8 space-y-4 border-t border-ink/15 pt-6">
            {[["Dimensions", p.dimensions], ["Material", p.material]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6">
                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 pt-1">{k}</dt>
                <dd className="text-sm text-right text-ink/80">{v}</dd>
              </div>
            ))}
          </dl>
          {!soldOut && (
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <div className="flex items-center border border-ink/25">
                <button data-testid="qty-minus" onClick={() => setQtyLocal((q) => Math.max(1, q - 1))} className="p-3 hover:text-brand transition-colors"><Minus size={14} /></button>
                <span data-testid="qty-value" className="font-mono text-sm w-8 text-center">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQtyLocal((q) => Math.min(p.quantity, q + 1))} className="p-3 hover:text-brand transition-colors"><Plus size={14} /></button>
              </div>
              <Magnetic>
                <button
                  data-testid="add-to-cart-btn"
                  onClick={() => { add(p, qty); toast.success(`${p.name} added to cart.`); }}
                  className="bg-ink text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors"
                >
                  Add to Cart
                </button>
              </Magnetic>
              {p.payment_link && (
                <Magnetic>
                  <a href={p.payment_link} target="_blank" rel="noreferrer" data-testid="buy-now-btn"
                    className="inline-flex items-center gap-3 bg-brand text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors">
                    Buy Now — {inr(p.price)} →
                  </a>
                </Magnetic>
              )}
            </div>
          )}
          {soldOut && (
            <Link to={`/contact?type=Other&subject=${encodeURIComponent(p.name)}`} data-testid="soldout-enquire-btn"
              className="mt-10 inline-flex items-center gap-3 border border-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-brand hover:text-brand transition-colors">
              Enquire about this piece
            </Link>
          )}
        </Reveal>
      </div>
    </main>
  );
}
